import { useEffect, useState } from 'react'
import { comparer, reaction } from 'mobx'
import { useWorkspace } from '../utils'
import { useCdtCodeMappingStore } from 'app/stores/StoreContext'
import { bg } from 'lib/background'
import type { CategoryResolver, CdtMappingLike } from 'lib/cdtCodeDefaults'

type CdtCodeMappingsState = { mappings: CdtMappingLike[]; loading: boolean; categoryOf: CategoryResolver }

/**
 * The workspace's CDT code mappings and the resolver built from them, read from the one
 * live copy in CdtCodeMappingStore. Safe in components that are not observer(): a MobX
 * reaction pushes store changes into React state.
 */
export function useCdtCodeMappings(): CdtCodeMappingsState {
  const workspace = useWorkspace()
  const store = useCdtCodeMappingStore()
  const [state, setState] = useState<CdtCodeMappingsState>(() => ({
    mappings: store.hits,
    loading: !!workspace?.id && store.list == null && store.listError == null,
    categoryOf: store.categoryOf,
  }))

  useEffect(
    () =>
      reaction(
        () => ({
          mappings: store.hits,
          loading: !!workspace?.id && store.list == null && store.listError == null,
          categoryOf: store.categoryOf,
        }),
        setState,
        { fireImmediately: true, equals: comparer.shallow },
      ),
    [store, workspace?.id],
  )

  useEffect(() => {
    if (workspace?.id) bg(store.ensureLoaded(workspace.id))
  }, [store, workspace?.id])

  return state
}
